import { motion } from 'framer-motion';
import { GitMerge } from 'lucide-react';

interface Props {
  stages: Array<{ stage: string; count: number; conversion: number }>;
}

const STAGE_COLOR: Record<string, string> = {
  Draft: 'bg-slate-400 dark:bg-slate-500',
  Ready: 'bg-blue-500',
  Started: 'bg-teal-500',
  Completed: 'bg-emerald-500'
};

export default function PipelineFunnel({ stages }: Props) {
  const max = Math.max(1, ...stages.map(s => s.count));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-800/60"
    >
      <div className="mb-4 flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-500/10">
          <GitMerge className="h-4 w-4 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Project Pipeline</h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Funnel from creation to completion</p>
        </div>
      </div>

      <div className="space-y-3">
        {stages.map((s, i) => {
          const pct = (s.count / max) * 100;
          const color = STAGE_COLOR[s.stage] ?? 'bg-slate-400';
          return (
            <div key={s.stage}>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{s.stage}</span>
                <span className="text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
                  <span className="font-bold text-slate-900 dark:text-white">{s.count}</span>
                  {i > 0 && (
                    <span className="ml-2">{s.conversion}% conv.</span>
                  )}
                </span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/40">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className={`h-full rounded-full ${color}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
